"use client";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES } from "@/libs/builder/consts";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const simplifiedPrizesInputs = [
  {
    name: "logoUrl",
    friendlyName: "RedeemPrizes Dialog Logo",
    type: "file",
    allowedFileTypes: IMAGE_FILE_TYPES,
    required: true,
  },
  {
    name: "logoAlt",
    friendlyName: "Alt Text for Logo",
    type: "string",
    defaultValue: "Logo Alt",
    required: true,
  },
  {
    name: "discoverUrlParent",
    friendlyName: "Discover button parent URL",
    type: "string",
  },
  {
    name: "prizeCardSizeVariant",
    friendlyName: "Prize Card Size Variant",
    type: "string",
    enum: ["default", "small"],
    defaultValue: "default",
  },
  {
    name: "prizeCardVariant",
    friendlyName: "Prize Card Variant",
    type: "string",
    enum: ["default", "blurredBackground"],
    defaultValue: "default",
  },
  {
    name: "isLotteryAddressRequirementDisabled",
    friendlyName: "Disable address requirement for lotteries",
    type: "boolean",
    defaultValue: false,
    helperText: "When enabled, lottery prizes will not require a delivery address. Physical prizes always require one.",
  },
  {
    name: "isFiltersEnabled",
    friendlyName: "Prize Filters Visible",
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "categoryFilterTitle",
    friendlyName: "Category filter title",
    type: "string",
    defaultValue: "Filters",
    showIf: 'options.get("isFiltersEnabled") === true',
  },
  {
    name: "allFiltersData",
    friendlyName: "'All' filters properties",
    type: "object",
    showIf: 'options.get("isFiltersEnabled") === true',
    subFields: [
      {
        name: "label",
        friendlyName: "Filter Label",
        type: "string",
      },
      {
        name: "iconUrl",
        friendlyName: "Filter Image",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "isDisabled",
        friendlyName: "Is filter hidden?",
        type: "boolean",
      },
    ],
    defaultValue: {
      label: "All",
      iconUrl: "",
      isDisabled: false,
    },
  },
  {
    name: "virtualFiltersData",
    friendlyName: "'Virtual' filters properties",
    type: "object",
    showIf: 'options.get("isFiltersEnabled") === true',
    subFields: [
      {
        name: "label",
        friendlyName: "Filter Label",
        type: "string",
      },
      {
        name: "iconUrl",
        friendlyName: "Filter Image",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "isDisabled",
        friendlyName: "Is filter hidden?",
        type: "boolean",
      },
    ],
    defaultValue: {
      label: "Virtual",
      iconUrl: "",
      isDisabled: false,
    },
  },
  {
    name: "physicalFiltersData",
    friendlyName: "'Physical' filters properties",
    type: "object",
    showIf: 'options.get("isFiltersEnabled") === true',
    subFields: [
      {
        name: "label",
        friendlyName: "Filter Label",
        type: "string",
      },
      {
        name: "iconUrl",
        friendlyName: "Filter Image",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "isDisabled",
        friendlyName: "Is filter hidden?",
        type: "boolean",
      },
    ],
    defaultValue: {
      label: "Physical",
      iconUrl: "",
      isDisabled: false,
    },
  },
  {
    name: "lotteryFiltersData",
    friendlyName: "'Lottery' filters properties",
    type: "object",
    showIf: 'options.get("isFiltersEnabled") === true',
    subFields: [
      {
        name: "label",
        friendlyName: "Filter Label",
        type: "string",
      },
      {
        name: "iconUrl",
        friendlyName: "Filter Image",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "isDisabled",
        friendlyName: "Is filter hidden?",
        type: "boolean",
      },
    ],
    defaultValue: {
      label: "Lottery",
      iconUrl: "",
      isDisabled: false,
    },
  },
  {
    name: "sensorialFiltersData",
    friendlyName: "'Sensorial' filters properties",
    type: "object",
    showIf: 'options.get("isFiltersEnabled") === true',
    subFields: [
      {
        name: "label",
        friendlyName: "Filter Label",
        type: "string",
      },
      {
        name: "iconUrl",
        friendlyName: "Filter Image",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "isDisabled",
        friendlyName: "Is filter hidden?",
        type: "boolean",
      },
    ],
    defaultValue: {
      label: "Sensorial",
      iconUrl: "",
      isDisabled: true,
    },
  },
  {
    name: "isAdditionalFiltersEnabled",
    friendlyName: "Rewards Filter and Sorting",
    type: "boolean",
    defaultValue: false,
  },
  // TODO: Additional filters drawer (sort by, category, subcategory, points range, club Ploom tiers, available for me).
  {
    name: "additionalFiltersData",
    friendlyName: "Rewards Filter and Sorting Options",
    type: "object",
    showIf: 'options.get("isAdditionalFiltersEnabled") === true',
    subFields: [
      {
        name: "subcategoryTranslations",
        friendlyName: "Subcategory Translations",
        helperText: "Translate dynamic OL subcategory names",
        type: "list",
        subFields: [
          {
            name: "key",
            friendlyName: "OL Subcategory Name",
            type: "string",
            required: true,
          },
          {
            name: "value",
            friendlyName: "Translated Label",
            type: "string",
            required: true,
          },
        ],
      },
      {
        name: "triggerButtonLabel",
        friendlyName: "Filters trigger button label",
        type: "string",
        required: true,
      },
      {
        name: "filtersDesktopTitle",
        friendlyName: "Filters title (desktop)",
        type: "string",
        required: true,
      },
      {
        name: "filtersSubtitle",
        friendlyName: "Filters subtitle",
        type: "string",
        required: true,
      },
      {
        name: "filtersDialogAriaLabel",
        friendlyName: "Filters dialog accessible label",
        helperText: "Accessible name announced for the filters dialog (screen readers).",
        type: "string",
        required: true,
      },
      {
        name: "resetFiltersButtonLabel",
        friendlyName: "Reset filters button label",
        type: "string",
        required: true,
      },
      {
        name: "isCategoryFilterEnabled",
        friendlyName: "Enable 'Category' filter",
        type: "boolean",
        defaultValue: true,
      },
      {
        name: "categoryFilterLabel",
        friendlyName: "Category filter label",
        type: "string",
      },
      {
        name: "isSubcategoryFilterEnabled",
        friendlyName: "Enable 'Subcategory' filter",
        type: "boolean",
        defaultValue: true,
        showIf: 'options.get("isCategoryFilterEnabled") === true',
      },
      {
        name: "subcategoryFilterLabel",
        friendlyName: "Subcategory filter label",
        type: "string",
      },
      {
        name: "isAvailableForMeFilterEnabled",
        friendlyName: "Enable 'Available for me' filter",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "availableForMeFilterLabel",
        friendlyName: "'Available for me' filter label",
        type: "string",
        required: true,
        showIf: 'options.get("isAvailableForMeFilterEnabled") === true',
      },
      {
        name: "availableForMeDisabledFilterTooltipText",
        friendlyName: "'Available for me' disabled filter tooltip",
        helperText: "ie. This filter is unavailable when '{0}' is selected.",
        type: "string",
        required: true,
        showIf: 'options.get("isAvailableForMeFilterEnabled") === true',
      },
      {
        name: "isSortByFilterEnabled",
        friendlyName: `Enable a "sort by" filter`,
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "sortByFilterLabel",
        friendlyName: `"Sort by" filter label`,
        type: "string",
        showIf: `options.get("isSortByFilterEnabled") === true`,
      },
      {
        name: "sortByFilterItems",
        friendlyName: `"Sort by" filter options`,
        type: "list",
        showIf: `options.get("isSortByFilterEnabled") === true`,
        subFields: [
          {
            name: "value",
            friendlyName: "Sorting option",
            type: "string",
            required: true,
            enum: [
              { label: "Price: low to high", value: "price-low-to-high" },
              { label: "Price: high to low", value: "price-high-to-low" },
              { label: "Name: A-Z", value: "name-asc" },
              { label: "Name: Z-A", value: "name-desc" },
              { label: "Newest first", value: "newest-first" },
              { label: "Expiring soon", value: "expiring-soon" },
            ],
          },
          {
            name: "displayName",
            friendlyName: "Display name",
            type: "string",
            required: true,
            helperText: "Name of the filter to be displayed to the user",
          },
        ],
      },
      {
        name: "isPointsRangeFilterEnabled",
        friendlyName: "Enable 'Points range' filter",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "pointsRangeFilterLabel",
        friendlyName: "Points range filter label",
        type: "string",
        required: true,
        showIf: 'options.get("isPointsRangeFilterEnabled") === true',
      },
      {
        name: "pointsRangeAllRangesLabel",
        friendlyName: "All range label",
        type: "string",
        required: true,
        showIf: 'options.get("isPointsRangeFilterEnabled") === true',
      },
      {
        name: "pointsRangeFilterItems",
        friendlyName: `Filter by range options`,
        type: "list",
        required: true,
        showIf: `options.get("isPointsRangeFilterEnabled") === true`,
        defaultValue: [
          { value: "1-100", displayName: "1-100 points" },
          { value: "101-200", displayName: "101-200 points" },
          { value: "200+", displayName: "200+ points" },
        ],
        onChange: (options: Map<string, unknown>) => {
          const items = options.get("pointsRangeFilterItems");
          if (Array.isArray(items) && items.length > 5) {
            options.set("pointsRangeFilterItems", items.slice(0, 5));
            alert("Maximum 5 point ranges allowed.");
          }
        },
        subFields: [
          {
            name: "value",
            friendlyName: "Filter range option",
            description: "ex. 1-100, 101-200, 200+",
            type: "string",
            required: true,
          },
          {
            name: "displayName",
            friendlyName: "Display name",
            type: "string",
            required: true,
            helperText: "Name of the filter to be displayed to the user",
          },
        ],
      },
      {
        name: "isClubPloomTiersFilterEnabled",
        friendlyName: "Enable 'Club Ploom tiers' filter",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "clubPloomTiersFilterLabel",
        friendlyName: "Club Ploom tiers filter label",
        type: "string",
      },
      {
        name: "silverTierLabel",
        friendlyName: "Silver tier label",
        type: "string",
        showIf: 'options.get("isClubPloomTiersFilterEnabled") === true',
      },
      {
        name: "goldTierLabel",
        friendlyName: "Gold tier label",
        type: "string",
        showIf: 'options.get("isClubPloomTiersFilterEnabled") === true',
      },
      {
        name: "blackTierLabel",
        friendlyName: "Black tier label",
        type: "string",
        showIf: 'options.get("isClubPloomTiersFilterEnabled") === true',
      },
      {
        name: "applyButtonLabel",
        friendlyName: "Apply filters button label",
        type: "string",
        required: true,
      },
    ],
  },
  {
    name: "pointsLabel",
    friendlyName: "Points Label",
    type: "string",
    defaultValue: "Points",
    required: true,
  },
  {
    name: "discoverButtonText",
    friendlyName: "Discover button text",
    type: "string",
  },
  {
    name: "earnPointsCtaText",
    friendlyName: "Earn points button text",
    type: "string",
    defaultValue: "Earn {#missingPoints#} to redeem.",
    required: true,
  },
  {
    name: "earnPointsCtaUrl",
    friendlyName: "Earn points button url",
    type: "string",
    required: true,
  },
  {
    name: "ctaLabel",
    friendlyName: "CTA Label",
    type: "string",
    defaultValue: "Redeem Now",
    required: true,
  },
  {
    name: "color",
    friendlyName: "Font color",
    type: "string",
    enum: [
      { label: "Light", value: "white" },
      { label: "Dark", value: "black" },
    ],
    defaultValue: "white",
  },
  {
    name: "noOffersText",
    friendlyName: "No Offers Text",
    type: "string",
    defaultValue: "No matching rewards found.",
    required: true,
  },
  {
    name: "noOffersSubtext",
    friendlyName: "No Offers Subtext",
    type: "string",
    defaultValue: "Adjust your filters to explore more rewards.",
  },
  {
    name: "newOfferDays",
    friendlyName: "New Offer Days",
    helperText: "How many days old prize is considered new",
    type: "number",
    defaultValue: 7,
  },
  {
    name: "newLabel",
    friendlyName: "New Label",
    type: "string",
    defaultValue: "New!",
    required: true,
  },
  {
    name: "oneDayLabel",
    friendlyName: "Last day label",
    type: "string",
    defaultValue: "Last day!",
  },
  {
    name: "daysLabel",
    friendlyName: "Days Label",
    type: "string",
    defaultValue: "{#days#} days left",
    helperText: "Uses placeholders for dynamic data such as: {#days#}",
  },
  {
    name: "pagination",
    friendlyName: "Pagination",
    type: "number",
    defaultValue: 3,
    required: true,
  },
  {
    name: "isTeaser",
    friendlyName: "Is Teaser",
    type: "boolean",
    defaultValue: true,
    required: true,
  },
  {
    name: "teaserData",
    friendlyName: "Teaser Data",
    type: "object",
    showIf: 'options.get("isTeaser") === true',
    subFields: [
      {
        name: "teaserTitle",
        friendlyName: "Teaser Title",
        type: "string",
        required: true,
      },
      {
        name: "teaserLinkUrl",
        friendlyName: "Teaser Link URL",
        type: "url",
        required: true,
      },
      {
        name: "teaserLinkText",
        friendlyName: "Teaser Link Text",
        type: "string",
        required: true,
      },
    ],
    defaultValue: {
      teaserTitle: "LATEST ADDITION TO REWARD’S CATALOGUE",
      teaserLinkUrl: "#",
      teaserLinkText: "View all rewards",
    },
    required: true,
  },
  {
    name: "loadMoreButtonLabel",
    friendlyName: "Load More Button Label",
    type: "string",
    defaultValue: "Load More",
    required: true,
  },
  {
    name: "errorMessage",
    friendlyName: "Error Message",
    type: "string",
    defaultValue: "Something went wrong, please try again later",
  },
  {
    name: "outOfStockLabel",
    friendlyName: "Out of Stock Label",
    type: "string",
    defaultValue: "Out of Stock",
    required: true,
  },
  {
    name: "quantityAriaLabel",
    type: "string",
    friendlyName: "Quantity input aria label",
    defaultValue: "Quantity",
  },
  {
    name: "increaseAriaLabel",
    type: "string",
    friendlyName: "Increase quantity aria label",
    defaultValue: "Increase quantity",
  },
  {
    name: "decreaseAriaLabel",
    type: "string",
    friendlyName: "Decrease quantity aria label",
    defaultValue: "Decrease quantity",
  },
  {
    name: "currentPositionButtonText",
    friendlyName: "Map: use current position button text",
    type: "string",
    defaultValue: "Use current position",
  },
  {
    name: "venuePopUpOffersCount",
    friendlyName: "Map: Venue popup offers count",
    type: "string",
    defaultValue: "Available rewards: {#count#}",
  },
  {
    name: "mapCategoryFilterTitle",
    friendlyName: "Map: Category filter title",
    type: "string",
    defaultValue: "Map: Filters",
  },
  {
    name: "backToListLabel",
    friendlyName: "Map: Back to list label",
    type: "string",
    defaultValue: "Back to list",
  },
  {
    name: "getDirectionLabel",
    friendlyName: "Map: Get direction label",
    type: "string",
    defaultValue: "Get directions",
  },
  {
    name: "storesCount",
    friendlyName: "Map: Stores count text",
    type: "string",
    defaultValue: "Stores",
  },
  {
    name: "searchButton",
    friendlyName: "Map: Search button text",
    type: "string",
    defaultValue: "Search",
  },
  {
    name: "rewardsText",
    friendlyName: "Map: rewards count text",
    type: "string",
    defaultValue: "Rewards",
  },
  {
    name: "searchBar",
    friendlyName: "Map: search bar placeholder",
    type: "string",
    defaultValue: "Search for stores",
  },
  {
    name: "searchHeader",
    friendlyName: "Map: search header",
    type: "string",
    defaultValue: "Search for stores",
  },
  {
    name: "flotiqApiKey",
    friendlyName: "Map: Flotiq api key",
    type: "string",
    defaultValue: "e3c2e3a790e8f8a058ca470a3540a5c4",
  },
  {
    name: "flotiqContentType",
    friendlyName: "Map: Flotiq content type",
    type: "string",
    defaultValue: "TrustedPlacesTest",
  },
  {
    name: "defaultLat",
    friendlyName: "Map: default latitude",
    type: "number",
    defaultValue: 51.507351,
  },
  {
    name: "defaultLng",
    friendlyName: "Map: default longitude",
    type: "number",
    defaultValue: -0.127758,
  },
  {
    name: "filtersTranslations",
    type: "list",
    friendlyName: "Map: Filters",
    subFields: [
      {
        name: "filterKey",
        type: "string",
        friendlyName: "Filter key defined in Flotiq",
        required: true,
      },
      {
        name: "translatedFilterKey",
        type: "string",
        friendlyName: "Translated filter key",
        required: true,
      },
      {
        name: "image",
        type: "file",
        friendlyName: "Image",
        allowedFileTypes: IMAGE_FILE_TYPES,
        required: true,
      },
    ],
    defaultValue: [
      {
        image:
          "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Fa9ff7d81326a40cba59334078ce44085",
        translatedFilterKey: "Restaurante",
        filterKey: "Restaurant",
      },
      {
        image:
          "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Fef1ae9af4604416fb7ee36a299883fb5",
        translatedFilterKey: "Bars",
        filterKey: "Bar",
      },
      {
        image:
          "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F8b96c3549e6c4cb587d485165e6da3bf",
        translatedFilterKey: "Cafes",
        filterKey: "Coffeshop",
      },
    ],
  },
] as const satisfies Inputs;

export const SimplifiedPrizesV2Component: RegisteredComponent = {
  name: "SimplifiedPrizesV2",
  component: dynamic(async () =>
    withValidation(
      simplifiedPrizesInputs,
      (await import("./SimplifiedPrizesV2")).SimplifiedPrizesV2,
      "SimplifiedPrizesV2"
    )
  ),
  inputs: simplifiedPrizesInputs,
  requiresParent: {
    query: {
      "component.name": { $in: ["LoyaltyWrapper"] },
    },
    message: "'SimplifiedPrizesV2' component can only be added inside a 'LoyaltyWrapper'",
  },
};
